import {
  ICallRecordingData,
  IClientPhoneApiCall,
  IClientPhoneCall,
  IMakeExternalCallResponse,
  IRestApiService,
} from '../types';
import { PhoneTracker } from '../services/analytics/apiTrackers';

class ClientPhoneApiCall implements IClientPhoneApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getClientPhoneCalls(ownerId: string | number, studioClientId: string | number): Promise<IClientPhoneCall[]> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/client/${studioClientId}`;

    return this.restApiService
      .get<IClientPhoneCall[]>(url)
      .then((result) => {
        PhoneTracker.trackGetClientPhoneCalls({
          ownerId,
          studioClientId,
          success: true,
          duration: Date.now() - startTime,
          callsCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetClientPhoneCalls({
          ownerId,
          studioClientId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getPendingClientPhoneCalls(ownerId: string | number, pendingClientId: string | number): Promise<IClientPhoneCall[]> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/pending-client/${pendingClientId}`;

    return this.restApiService
      .get<IClientPhoneCall[]>(url)
      .then((result) => {
        PhoneTracker.trackGetPendingClientPhoneCalls({
          ownerId,
          pendingClientId,
          success: true,
          duration: Date.now() - startTime,
          callsCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPendingClientPhoneCalls({
          ownerId,
          pendingClientId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getPhoneCalls(ownerId: string | number, from: any, to: any, callType: string): Promise<IClientPhoneCall[]> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/filtered?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}&callType=${callType}`;

    return this.restApiService
      .get<IClientPhoneCall[]>(url)
      .then((result) => {
        PhoneTracker.trackGetPhoneCalls({
          ownerId,
          from,
          to,
          callType,
          success: true,
          duration: Date.now() - startTime,
          callsCount: result?.length || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPhoneCalls({
          ownerId,
          from,
          to,
          callType,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getPhoneCallRecord(ownerId: string | number, callId: string | number): Promise<ICallRecordingData> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/call-record/${callId}`;

    return this.restApiService
      .get<ICallRecordingData>(url)
      .then((result) => {
        PhoneTracker.trackGetPhoneCallRecord({
          ownerId,
          callId,
          success: true,
          duration: Date.now() - startTime,
          hasRecording: result?.recordingUrl ? true : false,
          recordingDuration: result?.duration || 0,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackGetPhoneCallRecord({
          ownerId,
          callId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  makeExternalCall(
    ownerId: string | number,
    internalNumber: string,
    externalNumber: string
  ): Promise<IMakeExternalCallResponse> {
    const startTime = Date.now();
    const url = `client-phone-call/owner/${ownerId}/make-external-call`;

    return this.restApiService
      .post<IMakeExternalCallResponse>(url, { internalNumber, externalNumber })
      .then((result) => {
        PhoneTracker.trackMakeExternalCall({
          ownerId,
          internalNumber,
          externalNumber,
          success: true,
          duration: Date.now() - startTime,
          callId: result?.callId,
          callStatus: result?.status,
        });
        return result;
      })
      .catch((error) => {
        PhoneTracker.trackMakeExternalCall({
          ownerId,
          internalNumber,
          externalNumber,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }
}

export default ClientPhoneApiCall;
